import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Fingerprint, Package, Layers, Boxes, Database, ShieldCheck, Sparkles, Dna, GitCompare, CreditCard, Rocket, Terminal, BookOpen, ArrowRight } from "lucide-react";
import { Logo } from "@/components/Logo";
import { MatrixRain } from "@/components/MatrixRain";
import { NeonButton } from "@/components/Holo";
import { enterApp, marketingHref } from "@/lib/site";

const SECTIONS = [
  { id: "start", icon: Rocket, title: "Getting Started", accent: "turquoise", body: [
    "FUTRA OS™ is the operating system for AI creators. The core idea: your AI should never forget. Lock your characters, products and scenes once — stay consistent forever.",
    "Create an account, receive starter credits, then head to the Command Center. Each generation costs credits; failed generations are auto-refunded by Credit Shield™.",
  ]},
  { id: "identity", icon: Fingerprint, title: "Identity Matrix™", accent: "pink", body: [
    "Save a character by uploading reference images and locking attributes: Face, Skin Tone, Body Proportions, Hair, Makeup, Expressions, Personality.",
    "Add Character DNA™ (eye/nose/lip shape, jawline, muscle mass, height, body measurements, voice & walking style, energy level) to permanently lock identity across every generation.",
  ]},
  { id: "preview", icon: Sparkles, title: "Preview From Profile™", accent: "yellow", body: [
    "Instantly test a character's consistency before building a full campaign. FUTRA generates 7 shots — portrait, full body, sitting, walking, close-up beauty, alternate lighting and background.",
    "A Consistency Score (Face / Body / Skin / Hair + Overall) shows whether identity holds. Below 90% it flags DRIFT and suggests updating references.",
  ]},
  { id: "compare", icon: GitCompare, title: "Compare Mode", accent: "pink", body: [
    "Put your Master Character next to any generated frame. Face / Hair / Body / Clothing / Expression matches are scored, and changes are highlighted in red.",
    "Accept the generation to update character memory, or regenerate the frame until it locks.",
  ]},
  { id: "brand", icon: Package, title: "Brand Core™", accent: "turquoise", body: [
    "Lock product identity: dimensions, logos, packaging, colors and labels — so your product never resizes or morphs between shots.",
  ]},
  { id: "scene", icon: Layers, title: "Scene Memory™", accent: "green", body: [
    "Preserve continuity across a world: lighting, camera angle, environment, background details and props.",
  ]},
  { id: "world", icon: Boxes, title: "World Forge™", accent: "purple", body: [
    "Generate entire campaigns with locked consistency. Pick a type (TikTok, Commercial, Fashion, Movie Scene, Product Ad), lock identity/product/scene, add shots, and forge a full multi-shot set.",
    "Campaign Timeline™ lets you regenerate any single scene while keeping continuity. Version History saves, compares and restores results — so one bad frame never destroys a campaign.",
  ]},
  { id: "vault", icon: Database, title: "Memory Vault™", accent: "yellow", body: [
    "Everything you lock — characters, products, scenes, campaigns, renders — is saved and searchable in one place.",
  ]},
  { id: "shield", icon: ShieldCheck, title: "Credit Shield™", accent: "turquoise", body: [
    "Failed generations automatically return your credits. The refund ledger tracks every recovered credit.",
  ]},
  { id: "billing", icon: CreditCard, title: "Billing & Plans", accent: "yellow", body: [
    "Creator ($29/mo) and Studio ($99/mo) subscribe instantly via secure Stripe checkout. Enterprise is tailored — contact sales. Credits are granted on each successful billing cycle.",
  ]},
  { id: "api", icon: Terminal, title: "API (coming soon)", accent: "green", body: [
    "A public API at api.futra.pro is on the roadmap — programmatic access to Identity Matrix, campaigns and generation, so you can embed FUTRA memory into your own pipelines.",
  ]},
];

const ACCENT = { turquoise: "#00f0ff", pink: "#ff007f", green: "#39ff14", purple: "#b026ff", yellow: "#eaff00" };

export default function Docs() {
  const navigate = useNavigate();
  const [activeId, setActiveId] = useState("start");

  const go = (id) => { setActiveId(id); document.getElementById(`doc-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" }); };

  return (
    <div className="relative min-h-screen bg-[#050505] grain text-white">
      <div className="crt-overlay" />
      <nav className="fixed top-0 inset-x-0 z-50 glass border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
          <a href={marketingHref()}><Logo variant="gold" className="h-12" /></a>
          <div className="flex items-center gap-3">
            <span className="hidden sm:flex items-center gap-2 font-mono-tech text-[11px] uppercase tracking-widest text-neon-turquoise"><BookOpen size={13} /> Documentation Center</span>
            <NeonButton accent="turquoise" onClick={() => enterApp(navigate)} data-testid="docs-enter-btn">Enter FUTRA <ArrowRight size={14} /></NeonButton>
          </div>
        </div>
      </nav>

      <div className="absolute inset-0 top-16 opacity-40"><MatrixRain opacity={0.12} /></div>

      <div className="relative z-10 max-w-7xl mx-auto px-5 pt-28 pb-24 grid lg:grid-cols-[240px_1fr] gap-10">
        <aside className="hidden lg:block">
          <div className="sticky top-24 space-y-1">
            <p className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-neon-turquoise mb-4">// Docs</p>
            {SECTIONS.map((s) => (
              <button key={s.id} onClick={() => go(s.id)} data-testid={`docnav-${s.id}`}
                className="w-full flex items-center gap-2.5 px-3 py-2 font-mono-tech text-[11px] uppercase tracking-wider transition-colors text-left"
                style={activeId === s.id ? { color: ACCENT[s.accent], borderLeft: `2px solid ${ACCENT[s.accent]}`, background: "rgba(255,255,255,0.03)" } : { color: "rgba(255,255,255,0.55)", borderLeft: "2px solid transparent" }}>
                <s.icon size={14} strokeWidth={1.5} /> {s.title}
              </button>
            ))}
          </div>
        </aside>

        <main className="min-w-0">
          <p className="font-mono-tech text-neon-pink text-[11px] uppercase tracking-[0.3em] mb-3">// FUTRA OS™ Documentation</p>
          <h1 className="font-display font-black text-4xl sm:text-5xl">The AI That Finally Remembers.</h1>
          <p className="text-white/50 mt-4 font-mono-tech max-w-2xl">Everything you need to lock characters, products, scenes and campaigns — and keep them consistent across every generation.</p>

          <div className="mt-14 space-y-14">
            {SECTIONS.map((s, i) => (
              <motion.section key={s.id} id={`doc-${s.id}`} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                className="scroll-mt-24" data-testid={`doc-section-${s.id}`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center border border-white/15" style={{ color: ACCENT[s.accent] }}><s.icon size={19} strokeWidth={1.5} /></div>
                  <h2 className="font-display font-bold text-2xl">{s.title}</h2>
                </div>
                <div className="space-y-3 max-w-2xl">
                  {s.body.map((p, j) => <p key={j} className="text-white/60 font-mono-tech text-sm leading-relaxed">{p}</p>)}
                </div>
              </motion.section>
            ))}
          </div>

          <div className="mt-16 border-t border-white/10 pt-8">
            <NeonButton accent="pink" onClick={() => enterApp(navigate)} data-testid="docs-cta-btn"><Rocket size={15} /> Start Creating</NeonButton>
          </div>
        </main>
      </div>

      <footer className="relative z-10 border-t border-white/10 py-8 text-center font-mono-tech text-[11px] text-white/30">
        FUTRA OS™ · <a href={marketingHref()} className="text-white/50 hover:text-neon-turquoise transition-colors">futra.pro</a> · AI That Finally Remembers™
      </footer>
    </div>
  );
}
